import ConditionalToolTip from "components/design/conditionalToolTip";
import Loader from "components/DesignSystem/Loader/loader";
import Async from "components/DesignSystem/AsyncComponents/Async";
import { Button } from "components/DesignSystem/Button/Button";
import Modal from "components/DesignSystem/Modal/Modal";
import RadioGroup from "components/DesignSystem/RadioGroup/RadioGroup";
import { transactionColumn } from "components/Transaction/TransactionsTable";
import { ACCOUNTING_METHODS } from "constants/accountMethod";
import { DISPLAY_DATE, YYYY_MM_DD } from "constants/date";
import { LEDGER_TYPES } from "constants/ledgerEntryTypes";
import * as RECURRING_JOUNAL_ENTRY_UPDATE_TYPES from "constants/recurringJorunalEntryUpdateTypes";
import * as STOP_RECURRING_JOUNAL_ENTRY_TYPES from "constants/stopRecurringJounalEntryTypes";
import dayjs from "dayjs";
import { Form, Formik } from "formik";
import { journalEntrySchema } from "formValidations/JournalEntrySchema";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useModal } from "hooks/useModal";
import { useToast } from "hooks/useToast";
import { useState } from "react";
import {
  useGetRelatedLedgerEntryQuery,
  useUpdatedLedgerEntryMutation,
} from "store/apis/generalLedger";
import { RECURRING_JOUNRAL_ENTRY_UPDATE_TYPES_T } from "types/contants/recurringJournalEntryUpdateTypes";
import { STOP_RECURRING_JOUNAL_ENTRY_TYPES_T } from "types/contants/stopRecurringJounalEntryTypes";
import { BackendError } from "types/utils/error";
import { FileData } from "types/Models/books";
import { ModalProps } from "types/utils/modal";
import {
  getMaxExistingSerialNumber,
  isAccrualOnlyTransaction,
  isCashOnlyTransaction,
  maybeCopyAccrualToCash,
} from "utils/journalEntryUtils";
import { AddEditEntries } from "./AddEditEntries";
import { JournalEntryTransaction } from "./AddJournalEntryModal";
import { JournalEntryFormWrapper } from "./JournalEntryFormWrapper";
import { JournalEntryInfo } from "./JournalEntryInfo";
import LinkedInvoices from "./LinkedInvoices";
import { PayrollInfo } from "./LinkedPayroll";
import LinkedTransactions from "./LinkedTransactions";

const linkedTransactionColumn = [
  transactionColumn.date({ size: 10 }),
  transactionColumn.from({ size: 10 }),
  transactionColumn.merchant({ size: 30, disabled: true }),
  transactionColumn.description({ size: 30 }),
  transactionColumn.amount({ size: 20 }),
];

export const EditJournalEntry = ({
  close,
  showConfirmation,
  isOpen,
  dismissConfirmation,
  ledgerEntryId: editLedgerEntryId,
}: ModalProps & { ledgerEntryId: string }) => {
  const { alertToast, successToast } = useToast();
  const { uuid: groupId } = useCurrentGroupContext();
  const entityId = useCurrentEntityId();
  const [updatedLedgerEntry, { isLoading: updating }] =
    useUpdatedLedgerEntryMutation();
  const settingOnlyEditModal = useModal();
  const settingOrAndDataEditModal = useModal();
  const stopRecurringModal = useModal();

  const {
    data: relatedLedgerEntry,
    isLoading,
    isSuccess,
  } = useGetRelatedLedgerEntryQuery(
    {
      entityId,
      groupId,
      ledger_entry_id: editLedgerEntryId,
    },
    {
      skip: !entityId || !groupId || !editLedgerEntryId || !isOpen,
      refetchOnMountOrArgChange: true,
    }
  );

  const {
    ledger_entry,
    transactions = [],
    invoices,
  } = relatedLedgerEntry || {};
  const {
    entry_name = "",
    uuid: ledgerEntryId = "",
    linked_payroll_id,
  } = ledger_entry || {};

  const transaction_date = ledger_entry?.date
    ? dayjs(ledger_entry.date).format(DISPLAY_DATE)
    : null;

  const localTransactions: JournalEntryTransaction[] = transactions.map(
    ({
      transaction: {
        description,
        amount,
        uuid,
        memo,
        logo,
        merchant_uuid,
        ledger_entry_serial_number,
        classification_details,
      },
      cash_category_id,
      accrual_category_id,
      invoices,
      // basis field no longer exists in the new structure
    }) => {
      const classification_ids =
        classification_details?.flatMap(
          (group) => group.classes?.map((cls) => cls.uuid) || []
        ) || [];

      return {
        description: description || null,
        cash_category_id: cash_category_id || "",
        accrual_category_id: accrual_category_id || "",
        entry_type: cash_category_id
          ? ACCOUNTING_METHODS.CASH
          : ACCOUNTING_METHODS.ACCRUAL,
        merchant: merchant_uuid || null,
        uuid,
        memo: memo || null,
        logo: logo || null,
        debit: amount < 0 ? Math.abs(amount) : 0,
        credit: amount > 0 ? Math.abs(amount) : 0,
        id: uuid,
        invoice: invoices?.[0]
          ? {
              uuid: invoices?.[0]?.file_data?.uuid || "",
              file_type: invoices?.[0]?.file_data?.file_type || "IMAGE",
              is_previewable: invoices?.[0]?.file_data?.is_previewable || false,
              name: invoices?.[0]?.file_data?.name || "",
            }
          : null,
        ledger_entry_serial_number: ledger_entry_serial_number || "",
        classification_ids,
      };
    }
  );

  const isEmpty = transactions?.length === 0;

  let endsOnValue = null;

  if (ledger_entry?.settings?.end_date) {
    endsOnValue ??= "END_DATE";
  }

  if (
    ledger_entry?.settings?.max_recurring_count &&
    ledger_entry?.settings?.max_recurring_count >= 0
  ) {
    endsOnValue ??= "NO_OF_ENTRIES";
  }

  if (
    !ledger_entry?.settings?.end_date &&
    !ledger_entry?.settings?.max_recurring_count
  ) {
    endsOnValue ??= "NEVER_ENDS";
  }

  const initialValues = {
    transaction_date,
    ledgerEntryId,
    ledger_entry_name: entry_name,
    ledger_type: ledger_entry?.ledger_type || "",
    other_ledger_type_str: ledger_entry?.ledger_type_str || "",
    transactions: localTransactions,
    is_recurring_enabled: ledger_entry?.settings?.is_recurring_enabled || false,
    frequency: ledger_entry?.settings?.frequency,
    end_date: ledger_entry?.settings?.end_date,
    max_recurring_count: ledger_entry?.settings?.max_recurring_count,
    recurring_count: ledger_entry?.settings?.recurring_count,
    ends_on: endsOnValue,
    is_cash_same_as_accrual: ledger_entry?.is_cash_same_as_accrual,
    start_on: transaction_date,
    basis: ledger_entry?.basis || ACCOUNTING_METHODS.ACCRUAL,
    entry_level_invoice: null as Omit<
      FileData,
      "invoice_status" | "invoice_uuid"
    > | null,
  };

  const [tempFormData, setTempFormData] = useState<
    typeof initialValues | null
  >();

  const confirmSubmit = async ({
    values,
    editAaction,
    stopAction,
  }: {
    values: typeof initialValues;
    editAaction?: RECURRING_JOUNRAL_ENTRY_UPDATE_TYPES_T;
    stopAction?: STOP_RECURRING_JOUNAL_ENTRY_TYPES_T;
  }) => {
    const {
      ledgerEntryId,
      ledger_entry_name,
      transaction_date,
      transactions,
      ledger_type,
      other_ledger_type_str,
      is_cash_same_as_accrual,
      basis,
      entry_level_invoice,
    } = values;

    const transactionList = maybeCopyAccrualToCash(
      transactions,
      Boolean(is_cash_same_as_accrual)
    )
      .map(
        ({
          cash_category_id,
          accrual_category_id,
          merchant,
          uuid,
          credit,
          debit,
          invoice,
          ...rest
        }) => {
          const transaction_id = uuid.includes("NEW_ENTRY")
            ? {}
            : { transaction_id: uuid };

          return {
            ...rest,
            ...transaction_id,
            amount: Math.abs(Number(credit)) || Math.abs(Number(debit)) * -1,
            merchant_data_id: merchant,
            cash_category_id: cash_category_id || null,
            accrual_category_id: accrual_category_id || null,
            invoice_id: entry_level_invoice?.uuid || invoice?.uuid || null,
          };
        }
      )
      .filter((t) => {
        if (basis === ACCOUNTING_METHODS.CASH) {
          return isCashOnlyTransaction(t);
        }

        if (basis === ACCOUNTING_METHODS.ACCRUAL) {
          return isAccrualOnlyTransaction(t);
        }

        return true;
      });

    try {
      await updatedLedgerEntry({
        entityId,
        groupId,
        ledgerEntryId,
        transaction_date: dayjs(transaction_date).format(YYYY_MM_DD),
        ledger_entry_name,
        ledger_type: ledger_type || undefined,
        other_ledger_type_str: other_ledger_type_str || undefined,
        is_cash_same_as_accrual: Boolean(is_cash_same_as_accrual),
        transactions: transactionList,
        event_update_type:
          editAaction || RECURRING_JOUNAL_ENTRY_UPDATE_TYPES.UPDATE_CURRENT,
        stop_recurrence_type: stopAction,
        is_recurring_enabled: values.is_recurring_enabled,
        max_recurring_count:
          values.end_date || !values.max_recurring_count
            ? undefined
            : Number(values.max_recurring_count),
        frequency: values.frequency,
        end_date:
          values.end_date && !values.max_recurring_count
            ? dayjs(values.end_date).format(YYYY_MM_DD)
            : undefined,
        basis,
      }).unwrap();
      successToast({ message: "Journal entry edited" });
      close();
    } catch (error) {
      alertToast({
        message: (error as BackendError)?.data?.error?.message,
      });
    }
  };

  const onSubmit = async (values: typeof initialValues) => {
    let settingsChanged =
      values.frequency !== initialValues.frequency ||
      values.end_date !== initialValues.end_date ||
      values.max_recurring_count !== initialValues.max_recurring_count;

    let dataChanged =
      values.ledger_entry_name !== initialValues.ledger_entry_name ||
      JSON.stringify(values.transactions) !==
        JSON.stringify(initialValues.transactions) ||
      values.transaction_date !== initialValues.transaction_date;

    const newRecurring =
      values.is_recurring_enabled && !initialValues.is_recurring_enabled;
    const recurring = values.is_recurring_enabled;

    const stopsRecurring =
      initialValues.is_recurring_enabled && !values.is_recurring_enabled;

    if (stopsRecurring) {
      setTempFormData(values);
      return stopRecurringModal.open();
    }

    if (recurring && !newRecurring) {
      if (settingsChanged && !dataChanged) {
        setTempFormData(values);
        return settingOnlyEditModal.open();
      }

      if (dataChanged) {
        setTempFormData(values);
        return settingOrAndDataEditModal.open();
      }
    }

    return confirmSubmit({
      values,
      editAaction: RECURRING_JOUNAL_ENTRY_UPDATE_TYPES.UPDATE_CURRENT,
    });
  };

  if (isLoading) {
    return (
      <Modal.Root open>
        <Modal.Content size="xxxl">
          <Modal.Header>
            <Modal.Title>Edit Journal Entry</Modal.Title>
            <Modal.Close />
          </Modal.Header>
          <Modal.Body>
            <Loader />
          </Modal.Body>
        </Modal.Content>
      </Modal.Root>
    );
  }

  const onSettingOnlyEditModalSubmit = () => {
    if (tempFormData) {
      confirmSubmit({
        values: tempFormData,
        editAaction:
          RECURRING_JOUNAL_ENTRY_UPDATE_TYPES.UPDATE_CURRENT_AND_FOLLOWING,
      });
      settingOnlyEditModal.close();
      setTempFormData(null);
    }
  };

  const onSettingOrAndDataEditModalSubmit = ({
    event,
  }: {
    event: RECURRING_JOUNRAL_ENTRY_UPDATE_TYPES_T;
  }) => {
    if (tempFormData) {
      confirmSubmit({
        values: tempFormData,
        editAaction: event,
      });
      settingOrAndDataEditModal.close();
      setTempFormData(null);
    }
  };

  const onStopRecurringModalSubmit = ({
    event,
  }: {
    event: STOP_RECURRING_JOUNAL_ENTRY_TYPES_T;
  }) => {
    if (tempFormData) {
      confirmSubmit({
        values: tempFormData,
        stopAction: event,
      });
      stopRecurringModal.close();
      setTempFormData(null);
    }
  };

  return (
    <>
      <Formik
        initialValues={initialValues}
        onSubmit={onSubmit}
        enableReinitialize
        validateOnChange
        validationSchema={journalEntrySchema}
      >
        {({ submitForm, values, dirty, setFieldValue }) => (
          <>
            <Modal.Root
              open={isOpen}
              onOpenChange={() => {
                close({
                  takeConfirmation: dirty,
                });
              }}
            >
              <Form>
                <JournalEntryFormWrapper>
                  {({
                    isValidTransactions,
                    isAmountMatch,
                    cashAndAccrualMatch,
                  }) => (
                    <Modal.Content useCustomOverlay size="xxxl">
                      <Modal.Header>
                        <div>
                          <Modal.Title>Edit Journal Entry</Modal.Title>
                          <Modal.Subtitle>
                            Please make sure the total debit amount matches the
                            total credit amount
                          </Modal.Subtitle>
                        </div>
                        <Modal.Close />
                      </Modal.Header>
                      <Async.Root {...{ isEmpty, isLoading, isSuccess }}>
                        <Async.Empty>
                          <></>
                        </Async.Empty>
                        <Async.Success>
                          <Modal.Body className="t-gap-4 t-flex t-flex-col">
                            <div className="t-flex t-flex-col t-gap-4">
                              <JournalEntryInfo />
                              {linked_payroll_id && (
                                <PayrollInfo
                                  entityId={entityId}
                                  payrollId={linked_payroll_id}
                                />
                              )}
                              <LinkedTransactions
                                ledgerEntryId={editLedgerEntryId}
                                entityId={entityId}
                                columns={linkedTransactionColumn}
                              />

                              <LinkedInvoices
                                ledgerEntryId={editLedgerEntryId}
                                entityId={entityId}
                              />
                              <AddEditEntries
                                isEditFlow
                                cashAndAccrualMatch={cashAndAccrualMatch}
                                maxSerialNumber={getMaxExistingSerialNumber(
                                  localTransactions
                                )}
                              />
                            </div>
                          </Modal.Body>
                          <Modal.FooterButtonGroup>
                            <Button
                              type="reset"
                              onClick={() => close()}
                              disabled={updating}
                            >
                              Cancel
                            </Button>
                            <ConditionalToolTip
                              condition={
                                !isAmountMatch && (
                                  <span>
                                    Total debits and credits
                                    <br /> should match
                                  </span>
                                )
                              }
                            >
                              <span>
                                <Button
                                  customType="primary"
                                  type="submit"
                                  onClick={submitForm}
                                  disabled={!isValidTransactions || updating}
                                  isLoading={updating}
                                >
                                  Save changes
                                </Button>
                              </span>
                            </ConditionalToolTip>
                          </Modal.FooterButtonGroup>
                        </Async.Success>
                      </Async.Root>
                    </Modal.Content>
                  )}
                </JournalEntryFormWrapper>
              </Form>
            </Modal.Root>
          </>
        )}
      </Formik>

      <Modal.Root open={showConfirmation}>
        <Modal.Content>
          <Modal.Header>
            <Modal.Title>Your progress will be lost</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            Are you sure you want to exit? Your progress will not be saved.
          </Modal.Body>
          <Modal.FooterButtonGroup>
            <Button onClick={dismissConfirmation}>Cancel</Button>
            <Button onClick={() => close()} customType="danger">
              Exit
            </Button>
          </Modal.FooterButtonGroup>
        </Modal.Content>
      </Modal.Root>

      <Modal.Root
        open={settingOrAndDataEditModal.isOpen}
        onOpenChange={settingOrAndDataEditModal.close}
      >
        <Formik
          onSubmit={onSettingOrAndDataEditModalSubmit}
          initialValues={{
            event: RECURRING_JOUNAL_ENTRY_UPDATE_TYPES.UPDATE_CURRENT,
          }}
        >
          {({ submitForm, setFieldValue, values }) => (
            <Modal.Content>
              <Modal.Header>
                <Modal.Title>Edit Journal Entry</Modal.Title>
                <Modal.Close />
              </Modal.Header>
              <Modal.Body>
                <Form>
                  <p className="t-text-body-sm t-text-text-100 t-mb-4">
                    This is a recurring journal entry, this action will update:
                  </p>
                  <RadioGroup.Root
                    onValueChange={(v) => setFieldValue("event", v)}
                    value={values.event}
                    className="t-flex t-flex-col t-gap-4"
                  >
                    <RadioGroup.Item
                      value={RECURRING_JOUNAL_ENTRY_UPDATE_TYPES.UPDATE_CURRENT}
                    >
                      <span className="t-text-body-sm t-flex">This entry</span>
                    </RadioGroup.Item>
                    <RadioGroup.Item
                      value={
                        RECURRING_JOUNAL_ENTRY_UPDATE_TYPES.UPDATE_CURRENT_AND_FOLLOWING
                      }
                    >
                      <span className="t-text-body-sm t-flex">
                        This and following entries
                      </span>
                    </RadioGroup.Item>
                  </RadioGroup.Root>
                </Form>
              </Modal.Body>
              <Modal.FooterButtonGroup>
                <Modal.RawClose asChild>
                  <Button>Cancel</Button>
                </Modal.RawClose>
                <Button customType="primary" onClick={submitForm}>
                  Save
                </Button>
              </Modal.FooterButtonGroup>
            </Modal.Content>
          )}
        </Formik>
      </Modal.Root>

      <Modal.Root
        open={stopRecurringModal.isOpen}
        onOpenChange={stopRecurringModal.close}
      >
        <Formik
          onSubmit={onStopRecurringModalSubmit}
          initialValues={{
            event: STOP_RECURRING_JOUNAL_ENTRY_TYPES.STOP,
          }}
        >
          {({ submitForm, setFieldValue, values }) => (
            <Modal.Content>
              <Modal.Header>
                <Modal.Title>Edit Recurring Journal Entry</Modal.Title>
                <Modal.Close />
              </Modal.Header>
              <Modal.Body>
                <Form>
                  <p className="t-text-body-sm t-text-text-100 t-mb-4">
                    This is a recurring journal entry, this action will update:
                  </p>
                  <RadioGroup.Root
                    onValueChange={(v) => setFieldValue("event", v)}
                    value={values.event}
                    className="t-flex t-flex-col t-gap-4"
                  >
                    <RadioGroup.Item
                      value={STOP_RECURRING_JOUNAL_ENTRY_TYPES.STOP}
                    >
                      <span className="t-text-body-sm t-flex">
                        Stop recurrence
                      </span>
                    </RadioGroup.Item>
                    <RadioGroup.Item
                      value={STOP_RECURRING_JOUNAL_ENTRY_TYPES.STOP_AND_DELETE}
                    >
                      <span className="t-text-body-sm t-flex">
                        Delete related entries and stop recurrence
                      </span>
                    </RadioGroup.Item>
                  </RadioGroup.Root>
                </Form>
              </Modal.Body>
              <Modal.FooterButtonGroup>
                <Modal.RawClose asChild>
                  <Button>Cancel</Button>
                </Modal.RawClose>
                <Button customType="primary" onClick={submitForm}>
                  Save
                </Button>
              </Modal.FooterButtonGroup>
            </Modal.Content>
          )}
        </Formik>
      </Modal.Root>

      <Modal.Root
        open={settingOnlyEditModal.isOpen}
        onOpenChange={settingOnlyEditModal.close}
      >
        <Modal.Content>
          <Modal.Header>
            <Modal.Title>Edit Jounral Entry</Modal.Title>
            <Modal.Close />
          </Modal.Header>

          <Modal.Body>
            <p className="t-m-0 t-text-body-sm">
              This is a recurring journal entry, this action will update this
              and following entries. Are you sure?
            </p>
          </Modal.Body>
          <Modal.FooterButtonGroup>
            <Modal.RawClose asChild>
              <Button>Cancel</Button>
            </Modal.RawClose>

            <Button customType="primary" onClick={onSettingOnlyEditModalSubmit}>
              Confirm
            </Button>
          </Modal.FooterButtonGroup>
        </Modal.Content>
      </Modal.Root>
    </>
  );
};
